import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    PopupParagraph,
    PopupParagraphBold,
    PopupParagraphSmall,
    BoxWrapper,
    BoxTitle,
    LinkWrapper,
    NoWrapSpan,
} from 'src/domains/players/state/accountState/DepositLimitsUpdatePopups/DepositLimitUpdatePopup/DepositLimitUpdatePopup.style';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { UpdateLimitsComponent } from 'src/domains/players/state/accountState/DepositLimitsUpdatePopups/components/PopupContentWithInput/components/UpdateLimitsComponent/UpdateLimitsComponent';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface PopupContentWithInputProps {
    configComponents: ConfigComponents;
}

export const PopupContentWithInput = observer(
    'PopupContentWithInput',
    ({ configComponents }: PopupContentWithInputProps): JSX.Element => {
        const {
            config: { accountHelperMail, operatorFullName },
        } = configComponents;

        return (
            <>
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.intro'
                        defaultText='We are improving our deposit limits to give our clients a better user experience and extra control of their wagering. Gross Deposit Limits have been replaced by Net Deposit Limits.'
                    />
                </PopupParagraph>
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.current-limits'
                        defaultText='Currently, Deposit Limits are calculated on the total amount deposited within a selected calendar-based period.'
                    />
                </PopupParagraph>
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.net-limits'
                        defaultText='With Net Deposit Limits, the amount is calculated on the total amount deposited minus the total amount withdrawn within the selected calendar-based period.'
                    />
                </PopupParagraph>
                <PopupParagraphBold>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.formula'
                        defaultText='Net Deposits = (total of all deposits - total of all withdrawals) within the selected period'
                    />
                </PopupParagraphBold>
                <BoxWrapper>
                    <BoxTitle>
                        <I18n
                            langKey='deposit-limit-update.popup.box-title'
                            defaultText='Example'
                        />
                    </BoxTitle>
                    <PopupParagraphSmall>
                        <I18n
                            langKey='deposit-limit-update.popup.tertiary.box-line-1'
                            defaultText='You set a net deposit limit of £100 per month.'
                        />
                    </PopupParagraphSmall>
                    <PopupParagraphSmall>
                        <I18n
                            langKey='deposit-limit-update.popup.tertiary.box-line-2'
                            defaultText='You deposit £40'
                        />
                    </PopupParagraphSmall>
                    <PopupParagraphSmall>
                        <I18n
                            langKey='deposit-limit-update.popup.tertiary.box-line-3'
                            defaultText='You can then deposit a further £60 '
                        />
                        <NoWrapSpan>(£100 - £40 = £60)</NoWrapSpan>
                    </PopupParagraphSmall>
                    <PopupParagraphSmall>
                        <I18n
                            langKey='deposit-limit-update.popup.tertiary.box-line-4'
                            defaultText='You subsequently withdraw £40.'
                        />
                    </PopupParagraphSmall>
                    <PopupParagraphSmall>
                        <I18n
                            langKey='deposit-limit-update.popup.tertiary.box-line-5'
                            defaultText='You can then deposit a further £100 '
                        />
                        <NoWrapSpan>(£100 - £40 + £40 = £100)</NoWrapSpan>
                    </PopupParagraphSmall>
                </BoxWrapper>
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.review'
                        defaultText='We recommend you review your limits and consider whether they are at the right levels for you.'
                    />
                </PopupParagraph>
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.accept-info'
                        defaultText='If you understand our new improved net deposit limits and are happy with your current limits, shown below, you can click the Accept button and continue to safely enjoy everything {operatorFullName} has to offer.'
                        params={{ operatorFullName }}
                    />
                </PopupParagraph>
                <UpdateLimitsComponent configComponents={configComponents} />
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.contact-us-prefix'
                        defaultText='If you do not understand the new deposit limit system please do not click the Accept button, but '
                    />
                    <LinkWrapper to={`mailto:${accountHelperMail}`}>
                        <I18n
                            langKey='account.deposit-update.contact-us.email'
                            defaultText='contact us'
                            params={{ accountHelperMail }}
                        />
                    </LinkWrapper>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.contact-us-suffix'
                        defaultText=' for help.'
                    />
                </PopupParagraph>
                <PopupParagraph>
                    <I18n
                        langKey='deposit-limit-update.popup.tertiary.more-info'
                        defaultText='For more information, please visit our FAQ and Safer Gambling pages.'
                    />
                </PopupParagraph>
            </>
        );
    }
);
